import { useState } from 'react';

export const PATIENT_FORM_INITIAL_STATE = {
  name: '',
  title: '',
  email: '',
  mobile: '',
  address: '',
  date_of_birth: '',
  attendantName: '',
  attendantPhone: '',
  attendantRelation: '',
  insuranceAvailable: false,
  anyAllergies: false,
  uhidNo: '',
  sex: '',
  age: '',
  mentionAllergies: [],
  payment_method: '',
  online_payment_option: '',
  card_type: '',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.(com|in)$/i;

export const usePatientForm = (initialData = PATIENT_FORM_INITIAL_STATE) => {
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});

  const setInitialData = (dataOrUpdater) => setFormData(dataOrUpdater);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData(prev => {
      let newValue = value;

      if (type === 'checkbox') {
        newValue = checked;
        if (name === 'anyAllergies' && !checked) {
          return { ...prev, [name]: newValue, mentionAllergies: [] };
        }
      }

      if (name === 'mobile' || name === 'attendantPhone') {
        if (!/^[0-9+\s()-]*$/.test(value)) return prev;
      }

      if (name === 'name' || name === 'attendantName') {
        if (!/^[a-zA-Z\s]*$/.test(value)) return prev;
      }

      const updated = { ...prev, [name]: newValue };

      // Auto-calculate age from DOB
      if (name === 'date_of_birth') {
        const birthDate = new Date(value);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
        updated.age = age >= 0 ? age : '';
      }

      return updated;
    });

    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handlePhoneChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleEmailChange = (value) => {
    setFormData(prev => ({ ...prev, email: value }));
    if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
  };

  const handleAllergyChange = (newValue) => {
    setFormData(prev => ({ ...prev, mentionAllergies: Array.isArray(newValue) ? newValue : [] }));
  };

  const handleAttendantRelationChange = (value) => {
    setFormData(prev => ({ ...prev, attendantRelation: value || '' }));
    if (errors.attendantRelation) setErrors(prev => ({ ...prev, attendantRelation: '' }));
  };

  const handleOnlinePaymentChange = (value) => {
    setFormData(prev => ({ ...prev, online_payment_option: value, card_type: '' }));
  };

  const handleCardTypeChange = (value) => {
    setFormData(prev => ({ ...prev, card_type: value }));
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    let error = '';
    switch (name) {
      case 'name': if (!String(value).trim()) error = 'Name is required'; break;
      case 'title': if (!value) error = 'Title is required'; break;
      case 'email':
        if (!String(value).trim()) error = 'Email is required';
        else if (!EMAIL_REGEX.test(value)) error = 'Invalid email format (must end with .com or .in)';
        break;
      case 'mobile': if (!value || String(value).length < 10) error = 'Mobile number must be 10 digits'; break;
      case 'date_of_birth': if (!value) error = 'Date of birth is required'; break;
      case 'sex': if (!value) error = 'Sex is required'; break;
      case 'address': if (!String(value).trim()) error = 'Address is required'; break;
      case 'attendantName': if (!String(value).trim()) error = 'Attendant name is required'; break;
      case 'attendantPhone': if (!value || String(value).length < 10) error = 'Attendant phone must be 10 digits'; break;
      case 'attendantRelation': if (!String(value).trim()) error = 'Attendant relation is required'; break;
      case 'payment_method': if (!value) error = 'Payment method is required'; break;
      default: break;
    }
    if (error) setErrors(prev => ({ ...prev, [name]: error }));
  };

  const validate = (showNotification) => {
    const temp = {};
    if (!formData.name.trim()) temp.name = 'Name is required';
    if (!formData.title) temp.title = 'Title is required';
    if (!formData.email.trim()) temp.email = 'Email is required';
    else if (!EMAIL_REGEX.test(formData.email)) temp.email = 'Invalid email format (must end with .com or .in)';
    if (!formData.mobile || String(formData.mobile).length < 10) temp.mobile = 'Mobile number must be 10 digits';
    if (!formData.date_of_birth) temp.date_of_birth = 'Date of birth is required';
    if (!formData.age || formData.age < 0 || formData.age > 150) temp.age = 'Valid age is required';
    if (!formData.sex) temp.sex = 'Sex is required';
    if (!formData.address.trim()) temp.address = 'Address is required';
    if (!formData.attendantName.trim()) temp.attendantName = 'Attendant name is required';
    if (!formData.attendantPhone || String(formData.attendantPhone).length < 10) temp.attendantPhone = 'Attendant phone must be 10 digits';
    if (!formData.attendantRelation.trim()) temp.attendantRelation = 'Attendant relation is required';
    if (!formData.payment_method) temp.payment_method = 'Payment method is required';
    else if (formData.payment_method === 'Online' && !formData.online_payment_option) temp.online_payment_option = 'Please select an online payment option';
    else if (formData.payment_method === 'Online' && formData.online_payment_option === 'card' && !formData.card_type) temp.card_type = 'Please select card type';

    setErrors(temp);
    if (Object.keys(temp).length > 0) {
      if (showNotification) showNotification('error', `Required: ${Object.values(temp)[0]}`);
      return false;
    }
    return true;
  };

  const buildPayload = (finalMentionAllergies) => ({
    title: formData.title,
    name: formData.name,
    email: formData.email,
    date_of_birth: formData.date_of_birth,
    mobile: formData.mobile,
    address: formData.address,
    attendant_name: formData.attendantName,
    attendant_phone: formData.attendantPhone,
    attendant_relation: formData.attendantRelation,
    insurance_available: formData.insuranceAvailable ? 1 : 0,
    any_allergies: formData.anyAllergies ? 1 : 0,
    mention_allergies: (finalMentionAllergies || formData.mentionAllergies).join(', '),
    uhid_no: formData.uhidNo,
    age: formData.age,
    sex: formData.sex,
    payment_method: formData.payment_method || 'Cash',
    online_payment_option: formData.online_payment_option || 'upi',
    card_type: formData.card_type || null,
  });

  return {
    formData,
    errors,
    setInitialData,
    handleChange,
    handlePhoneChange,
    handleEmailChange,
    handleAllergyChange,
    handleAttendantRelationChange,
    handleOnlinePaymentChange,
    handleCardTypeChange,
    handleBlur,
    validate,
    buildPayload,
  };
};
